import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const ThemeContext = createContext();

// Safe check for browser environment
const isBrowser = typeof window !== 'undefined';

// ─── CSS Custom Property Maps ────────────────────────────────────────────────
const CSS_VARS = {
  light: {
    '--color-bg': '#F3F4F6',
    '--color-surface': '#FFFFFF',
    '--color-elevated': '#FFFFFF',
    '--color-card': '#FFFFFF',
    '--color-text-primary': '#111827',
    '--color-text-secondary': '#6B7280',
    '--color-text-muted': '#9CA3AF',
    '--color-border': '#E5E7EB',
    '--color-border-subtle': '#F3F4F6',
    '--color-accent': '#2563EB',
    '--color-accent-hover': '#1D4ED8',
    '--color-danger': '#DC2626',
    '--color-success': '#16A34A',
    '--color-warning': '#D97706',
    '--shadow-sm': '0 1px 2px rgba(0,0,0,0.05)',
    '--shadow-md': '0 4px 6px rgba(0,0,0,0.07)',
    '--shadow-lg': '0 10px 15px rgba(0,0,0,0.1)',
  },
  dark: {
    '--color-bg': '#111827',
    '--color-surface': '#1A2535',
    '--color-elevated': '#1F2937',
    '--color-card': '#253345',
    '--color-text-primary': '#F9FAFB',
    '--color-text-secondary': '#D1D5DB',
    '--color-text-muted': '#9CA3AF',
    '--color-border': '#374151',
    '--color-border-subtle': '#1F2937',
    '--color-accent': '#3B82F6',
    '--color-accent-hover': '#60A5FA',
    '--color-danger': '#EF4444',
    '--color-success': '#22C55E',
    '--color-warning': '#F59E0B',
    '--shadow-sm': '0 1px 2px rgba(0,0,0,0.3)',
    '--shadow-md': '0 4px 6px rgba(0,0,0,0.4)',
    '--shadow-lg': '0 10px 15px rgba(0,0,0,0.5)',
  },
  'high-contrast': {
    '--color-bg': '#000000',
    '--color-surface': '#1A1A1A',
    '--color-elevated': '#262626',
    '--color-card': '#333333',
    '--color-text-primary': '#FFFFFF',
    '--color-text-secondary': '#E5E5E5',
    '--color-text-muted': '#CCCCCC',
    '--color-border': '#666666',
    '--color-border-subtle': '#444444',
    '--color-accent': '#5B9DFF',
    '--color-accent-hover': '#80B3FF',
    '--color-danger': '#FF6666',
    '--color-success': '#66FF66',
    '--color-warning': '#FFB84D',
    '--shadow-sm': '0 0 0 1px rgba(255,255,255,0.15)',
    '--shadow-md': '0 0 0 1px rgba(255,255,255,0.2)',
    '--shadow-lg': '0 0 0 2px rgba(255,255,255,0.25)',
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const getSavedTheme = () => {
  if (!isBrowser) return null;
  try { return localStorage.getItem('theme'); } catch { return null; }
};

const getSystemPreference = () => {
  if (!isBrowser) return 'light';
  try {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch { return 'light'; }
};

const prefersReducedMotion = () => {
  if (!isBrowser) return false;
  try { return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

// ─── Theme Analytics ─────────────────────────────────────────────────────────
const _themeAnalytics = { changes: 0, lastChanged: null, history: [] };

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [systemTheme, setSystemTheme] = useState('light');
  const [isHydrated, setIsHydrated] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [transitionsEnabled, setTransitionsEnabled] = useState(true);

  // ── Hydration: sync from localStorage/system after mount ──
  useEffect(() => {
    const savedTheme = getSavedTheme();
    if (savedTheme) {
      setTheme(savedTheme);
    } else {
      const systemPref = getSystemPreference();
      setTheme(systemPref === 'dark' ? 'dark' : 'light');
    }
    setSystemTheme(getSystemPreference());
    setReducedMotion(prefersReducedMotion());
    setIsHydrated(true);
  }, []);

  // ── Listen for system theme changes ──
  useEffect(() => {
    if (!isBrowser) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handle = (e) => setSystemTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener?.('change', handle) ?? mq.addListener?.(handle);
    return () => mq.removeEventListener?.('change', handle) ?? mq.removeListener?.(handle);
  }, []);

  // ── Listen for reduced-motion changes ──
  useEffect(() => {
    if (!isBrowser) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handle = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.('change', handle) ?? mq.addListener?.(handle);
    return () => mq.removeEventListener?.('change', handle) ?? mq.removeListener?.(handle);
  }, []);

  // ── Resolve effective theme ──
  const effectiveTheme = useMemo(() => {
    if (theme === 'system') return systemTheme;
    return theme;
  }, [theme, systemTheme]);

  // ── Apply theme class + CSS custom properties ──
  useEffect(() => {
    if (!isBrowser) return;
    const root = document.documentElement;

    // Remove all theme classes
    root.classList.remove('light', 'dark', 'high-contrast');
    // Map high-contrast to dark class for Tailwind
    root.classList.add(effectiveTheme === 'high-contrast' ? 'dark' : effectiveTheme);

    // Inject CSS custom properties
    const vars = CSS_VARS[effectiveTheme] || CSS_VARS.light;
    for (const [prop, val] of Object.entries(vars)) {
      root.style.setProperty(prop, val);
    }

    // Transition control
    if (transitionsEnabled && !reducedMotion) {
      root.style.setProperty('--theme-transition', 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease');
    } else {
      root.style.setProperty('--theme-transition', 'none');
    }

    // Save to localStorage (only after hydration)
    if (isHydrated) {
      try { localStorage.setItem('theme', theme); } catch { /* unavailable */ }
    }
  }, [effectiveTheme, theme, isHydrated, transitionsEnabled, reducedMotion]);

  // ── Theme switchers ──
  const setThemeWithAnalytics = useCallback((newTheme) => {
    setTheme(newTheme);
    _themeAnalytics.changes++;
    _themeAnalytics.lastChanged = Date.now();
    _themeAnalytics.history.push({ theme: newTheme, at: Date.now() });
    if (_themeAnalytics.history.length > 20) _themeAnalytics.history.shift();
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeWithAnalytics((prev) => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'system';
      return 'light';
    });
  }, [setThemeWithAnalytics]);

  const setLightTheme = useCallback(() => setThemeWithAnalytics('light'), [setThemeWithAnalytics]);
  const setDarkTheme = useCallback(() => setThemeWithAnalytics('dark'), [setThemeWithAnalytics]);
  const setSystemThemeMode = useCallback(() => setThemeWithAnalytics('system'), [setThemeWithAnalytics]);
  const setHighContrast = useCallback(() => setThemeWithAnalytics('high-contrast'), [setThemeWithAnalytics]);

  /** Enable/disable CSS transition animations on theme change */
  const setThemeTransitions = useCallback((enabled) => setTransitionsEnabled(enabled), []);

  /** Get analytics about theme usage */
  const getThemeAnalytics = useCallback(() => ({ ..._themeAnalytics }), []);

  /** Get the CSS custom property value for current theme */
  const getCSSVar = useCallback((name) => {
    const vars = CSS_VARS[effectiveTheme] || CSS_VARS.light;
    return vars[name] || null;
  }, [effectiveTheme]);

  const value = useMemo(() => ({
    theme,
    effectiveTheme,
    isLight: effectiveTheme === 'light',
    isDark: effectiveTheme === 'dark' || effectiveTheme === 'high-contrast',
    isSystem: theme === 'system',
    isHighContrast: effectiveTheme === 'high-contrast',
    reducedMotion,
    transitionsEnabled,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    setSystemTheme: setSystemThemeMode,
    setHighContrast,
    setThemeTransitions,
    getThemeAnalytics,
    getCSSVar,
  }), [
    theme, effectiveTheme, reducedMotion, transitionsEnabled,
    toggleTheme, setLightTheme, setDarkTheme, setSystemThemeMode,
    setHighContrast, setThemeTransitions, getThemeAnalytics, getCSSVar,
  ]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
